import { useAddresses as useAddressContext } from '../contexts/AddressContext';
import { addressAPI, addressUtils, keyboardNavigation, clickOutsideHandler } from '../utils/addressUtils';
import { useState, useCallback, useRef, useEffect } from 'react';

export const useAddresses = () => {
  const addressContext = useAddressContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationError, setOperationError] = useState(null);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filtered addresses for dropdown
  const filteredAddresses = addressUtils.getDropdownAddresses(
    addressContext.addresses,
    searchTerm,
    10
  );

  // CRUD operations
  const addAddress = useCallback(async (addressData) => {
    setOperationLoading(true);
    setOperationError(null);
    
    try {
      const validation = addressUtils.validateAddress(addressData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      await addressAPI.add(addressData);
      await addressContext.refreshAddresses(); // Refresh to get latest data
      return true;
    } catch (error) {
      setOperationError(error.message);
      throw error;
    } finally {
      setOperationLoading(false);
    }
  }, [addressContext]);

  const updateAddress = useCallback(async (id, addressData) => {
    setOperationLoading(true);
    setOperationError(null);
    
    try {
      const validation = addressUtils.validateAddress(addressData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      await addressAPI.update(id, addressData);
      await addressContext.refreshAddresses(); // Refresh to get latest data
      return true;
    } catch (error) {
      setOperationError(error.message);
      throw error;
    } finally {
      setOperationLoading(false);
    }
  }, [addressContext]);

  const deleteAddress = useCallback(async (id) => {
    setOperationLoading(true);
    setOperationError(null);
    
    try {
      await addressAPI.delete(id);
      await addressContext.refreshAddresses(); // Refresh to get latest data
      return true;
    } catch (error) {
      setOperationError(error.message);
      throw error;
    } finally {
      setOperationLoading(false);
    }
  }, [addressContext]);

  const uploadCSV = useCallback(async (file, mode = 'append') => {
    setOperationLoading(true);
    setOperationError(null);
    
    try {
      await addressAPI.uploadCSV(file, mode);
      await addressContext.refreshAddresses(); // Refresh to get latest data
      return true;
    } catch (error) {
      setOperationError(error.message);
      throw error;
    } finally {
      setOperationLoading(false);
    }
  }, [addressContext]);

  // Dropdown management
  const selectAddress = useCallback((address) => {
    setSelectedAddress(address);
    setSearchTerm(address.address);
    setDropdownOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const handleInputChange = useCallback((value) => {
    setSearchTerm(value);
    setDropdownOpen(true);
    setHighlightedIndex(-1);
  }, []);

  const handleInputFocus = useCallback(() => {
    setDropdownOpen(true);
  }, []);

  const handleDropdownClose = useCallback(() => {
    setDropdownOpen(false);
    setHighlightedIndex(-1);
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback((event) => {
    keyboardNavigation.handleKeyDown(event, {
      dropdownOpen,
      highlightedIndex,
      filteredItems: filteredAddresses,
      onArrowDown: setHighlightedIndex,
      onArrowUp: setHighlightedIndex,
      onEnter: selectAddress,
      onEscape: handleDropdownClose
    });
  }, [dropdownOpen, highlightedIndex, filteredAddresses, selectAddress, handleDropdownClose]);

  // Click outside handler
  useEffect(() => {
    const handler = clickOutsideHandler.createHandler({
      dropdownRef,
      inputRef,
      onClose: handleDropdownClose
    });

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [handleDropdownClose]);

  return {
    // Context data
    addresses: addressContext.addresses,
    loading: addressContext.loading,
    error: addressContext.error,
    lastFetched: addressContext.lastFetched,
    
    // Local state
    searchTerm,
    dropdownOpen,
    highlightedIndex,
    selectedAddress,
    operationLoading,
    operationError,
    
    // Refs
    inputRef,
    dropdownRef,
    
    // Filtered data
    filteredAddresses,
    
    // CRUD operations
    addAddress,
    updateAddress,
    deleteAddress,
    uploadCSV,
    refreshAddresses: addressContext.refreshAddresses,
    
    // Dropdown management
    selectAddress,
    handleInputChange,
    handleInputFocus,
    handleDropdownClose,
    handleKeyDown,
    
    // Utility functions
    findById: (id) => addressUtils.findById(addressContext.addresses, id),
    findByAddress: (address) => addressUtils.findByAddress(addressContext.addresses, address),
    filterBySearch: (term) => addressUtils.filterBySearch(addressContext.addresses, term),
    sortByLabel: () => addressUtils.sortByLabel(addressContext.addresses),
    validateAddress: addressUtils.validateAddress
  };
};
